import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import {
  emptyEmployee,
  IEmployee,
  TYPE_EMPLOYEE,
} from "../../../types/employee.type";
import InputFormik from "../../../common/components/form/InputFormik";
import ButtonWithLoader from "../../../common/components/ui/loading/ButtonLoading";
import ComboBoxFormik from "../../../common/components/form/ComboBoxFormik";
import Permission from "@/common/components/auth/Permission";
import { useGetCompanies } from "@/common/hooks";
import { maskPhone } from "@/common/helpers/mask.helpers";

type FormPropsCreatePartners = {
  onSubmit: (
    values: IEmployee
  ) => Promise<{ ok: boolean; data: IEmployee; errors?: unknown[] }>;
  data: IEmployee;
  formRequestErrors?: { message: string; path: string[] }[];
};

const validationSchema = Yup.object({
  name: Yup.string().required("Nome é obrigatório."),
  type: Yup.string().required("Tipo é obrigatório."),
  email: Yup.string().email("Email inválido").required("Email é obrigatório"),
  position: Yup.string().required("Cargo é obrigatório."),
  active: Yup.boolean(),
  phone: Yup.string(),
  companyId: Yup.string().required("Cidade é obrigatória."),
});

function FormEditEmployeeData(props: FormPropsCreatePartners) {
  const [isCreating, setIsCreating] = useState(false);
  const { data: companies } = useGetCompanies();

  const formik = useFormik({
    initialValues: isCreating ? emptyEmployee : props.data || emptyEmployee,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (data: IEmployee) => {
      const result = await props.onSubmit(data);
      if (result.ok && result.data) {
        formik.setValues(result.data);
      }
      return result;
    },
  });
  const companyOptions =
    companies?.map((company) => ({
      value: company._id ?? "",
      label: company.address.city,
    })) || [];
  useEffect(() => {
    setIsCreating(!props.data);
  }, [props.data]);

  return (
    <form onSubmit={formik.handleSubmit} className="w-full">
      <FormikProvider value={formik}>
        <section className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-semibold mb-4">Informações Pessoais</h2>
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="grid grid-cols-1 text-sm lg:text-lg col-span-3 gap-2 sm:grid-cols-2 xl:grid-cols-3">
              <InputFormik label="Nome do Colaborador:" name="name" required />
              <InputFormik
                label="E-mail do Colaborador:"
                name="email"
                required
              />
              <InputFormik
                label="Telefone do Colaborador:"
                name="phone"
                required
                mask={maskPhone}
              />

              <ComboBoxFormik
                name="type"
                label="Tipo:"
                disabled={!!props.data._id}
                options={[
                  { value: "", label: "Selecione o tipo..." },
                  {
                    value: TYPE_EMPLOYEE.OPERADOR,
                    label: TYPE_EMPLOYEE.OPERADOR,
                  },
                  {
                    value: TYPE_EMPLOYEE.MECHANIC,
                    label: TYPE_EMPLOYEE.MECHANIC,
                  },
                  {
                    value: TYPE_EMPLOYEE.MANAGER,
                    label: TYPE_EMPLOYEE.MANAGER,
                  },
                  {
                    value: TYPE_EMPLOYEE.CONSULTANT,
                    label: TYPE_EMPLOYEE.CONSULTANT,
                  },
                  { value: TYPE_EMPLOYEE.SDR, label: TYPE_EMPLOYEE.SDR },
                ]}
              />

              <ComboBoxFormik
                required
                name="position"
                label="Cargo do Colaborador:"
                options={[
                  { value: "", label: "Selecione o cargo..." },
                  { value: "Consultor", label: "Consultor" },
                  { value: "SDR", label: "SDR" },
                  { value: "Mecânico", label: "Mecânico" },
                  { value: "Chefe de oficina", label: "Chefe de oficina" },
                  { value: "Gerente de cidade", label: "Gerente de cidade" },
                  { value: "Preparador", label: "Preparador" },
                  { value: "Pós-Atendimento", label: "Pós-Atendimento" },
                  { value: "Marketing", label: "Marketing" },
                  { value: "Estoquista", label: "Estoquista" },
                  { value: "RH", label: "RH" },
                ]}
              />
              <ComboBoxFormik
                required
                name="companyId"
                label="Cidade:"
                options={[
                  { value: "", label: "Selecione a cidade..." },
                  ...companyOptions,
                ]}
              />

              <div className="col-span-3 mt-4 flex items-center gap-3">
                <div className="col-span-3 mt-2 flex items-center gap-2">
                  <input
                    id="active"
                    type="checkbox"
                    {...formik.getFieldProps("active")}
                    checked={formik.values.active}
                    className="w-4 h-4"
                  />
                  <label htmlFor="active" className="text-sm">
                    Colaborador Ativo
                  </label>
                </div>
                <Permission role="admin">
                  <div className="col-span-3 mt-2 flex items-center gap-2">
                    <input
                      id="viewAll"
                      type="checkbox"
                      {...formik.getFieldProps("viewAll")}
                      checked={formik.values.viewAll}
                      className="w-4 h-4"
                    />
                    <label htmlFor="viewAll" className="text-sm">
                      Visualizar todas empressas
                    </label>
                  </div>
                </Permission>
              </div>
            </div>
          </div>
        </section>
        <div className="w-full px-6 pb-4 flex justify-end">
          <ButtonWithLoader
            isLoading={formik.isSubmitting}
            disabled={!formik.isValid}
            onClick={formik.handleSubmit}
          >
            Salvar
          </ButtonWithLoader>
        </div>
      </FormikProvider>
    </form>
  );
}

export default FormEditEmployeeData;
